import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  uploadImage,
  upscaleImage,
  downloadImage,
  createMockUpscaledImage,
  formatFileSize,
  estimateUpscaledSize,
} from '../services/imageUpscaler';
import { getToken } from '../services/api';

const ImageUpscaler = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [upscaleOptions, setUpscaleOptions] = useState({
    scale: 2,
    format: 'png',
    quality: 'high',
  });
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [upscaleProgress, setUpscaleProgress] = useState(0);
  const [upscaledResult, setUpscaledResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [compareSliderPosition, setCompareSliderPosition] = useState(50);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const fileInputRef = useRef(null);
  const compareContainerRef = useRef(null);

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      await handleImageUpload(files[0]);
    }
  };

  const handleFileInputChange = async (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      await handleImageUpload(files[0]);
    }
  };

  const handleImageUpload = async (file) => {
    setError(null);
    try {
      const imageData = await uploadImage(file);
      setUploadedImage(imageData);
      setUpscaledResult(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpscaleClick = async () => {
    if (!uploadedImage) return;

    setIsUpscaling(true);
    setError(null);
    setUpscaleProgress(0);

    try {
      const result = await upscaleImage({
        imageId: uploadedImage.id,
        scale: upscaleOptions.scale,
        format: upscaleOptions.format,
        quality: upscaleOptions.quality,
        onProgress: (progress) => {
          setUpscaleProgress(progress.progress);
        },
      });

      // Create mock upscaled image using canvas
      const upscaledDataUrl = await createMockUpscaledImage(
        uploadedImage.dataUrl,
        upscaleOptions.scale,
        upscaleOptions.format
      );

      setUpscaledResult({
        ...result,
        dataUrl: upscaledDataUrl,
        width: uploadedImage.width * upscaleOptions.scale,
        height: uploadedImage.height * upscaleOptions.scale,
      });
      fetchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpscaling(false);
      setUpscaleProgress(0);
    }
  };

  const handleDownloadUpscaled = () => {
    if (!upscaledResult) return;

    const filename = `upscaled_${upscaleOptions.scale}x_${uploadedImage.name.split('.')[0]}.${upscaleOptions.format}`;
    downloadImage(upscaledResult.dataUrl, filename);
  };

  const handleNewUpload = () => {
    setUploadedImage(null);
    setUpscaledResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCompareSlider = (e) => {
    if (!compareContainerRef.current) return;

    const rect = compareContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setCompareSliderPosition(percentage);
  };

  const handleCompareSliderTouch = (e) => {
    if (!compareContainerRef.current) return;

    const touch = e.touches[0];
    const rect = compareContainerRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setCompareSliderPosition(percentage);
  };

  const fetchHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      const token = getToken();
      if (!token) return;
      const res = await fetch('/api/ai/history?type=upscale&limit=10', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setHistory(data.jobs || []);
      }
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return (
    <div className="p-6 px-8 max-w-[1200px] mx-auto">
      {!uploadedImage ? (
        // Upload section
        <section className="py-5">
          <div
            className={`bg-white border-[3px] border-dashed rounded-2xl p-[60px] px-10 text-center cursor-pointer transition-all duration-300
              ${dragActive ? 'border-blue-500 bg-blue-100 scale-[1.02]' : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="text-6xl mb-5">📁</div>
            <h3 className="text-2xl text-gray-900 mb-2">이미지를 업로드하세요</h3>
            <p className="text-base text-gray-500 mb-5">이미지를 드래그하거나 클릭하여 선택하세요</p>
            <div className="flex gap-5 justify-center mt-5">
              <span className="text-sm text-gray-500">지원 형식: JPG, PNG, WebP</span>
              <span className="text-sm text-gray-500">최대 크기: 10MB</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 py-3 px-4 bg-red-50 text-red-700 rounded-xl my-4 text-sm">
              <span className="text-lg">⚠️</span>
              {error}
            </div>
          )}
        </section>
      ) : (
        <>
          {/* Options and preview section */}
          <section className="grid grid-cols-2 gap-6 mb-6 max-md:grid-cols-1">
            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
              <h3 className="text-lg text-gray-900 mb-5">업스케일 옵션</h3>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2.5">확대 배율</label>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 border-2
                      ${upscaleOptions.scale === 2
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 border-gray-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, scale: 2 })}
                    disabled={isUpscaling}
                  >
                    2x
                  </button>
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 border-2
                      ${upscaleOptions.scale === 4
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 border-gray-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, scale: 4 })}
                    disabled={isUpscaling}
                  >
                    4x
                  </button>
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2.5">출력 포맷</label>
                <div className="flex gap-2 max-[480px]:flex-col">
                  {['png', 'jpg', 'webp'].map((format) => (
                    <button
                      key={format}
                      className={`flex-1 p-3 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 border-2
                        ${upscaleOptions.format === format
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-gray-100 border-gray-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                      onClick={() => setUpscaleOptions({ ...upscaleOptions, format })}
                      disabled={isUpscaling}
                    >
                      {format.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2.5">처리 품질</label>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 border-2
                      ${upscaleOptions.quality === 'fast'
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 border-gray-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, quality: 'fast' })}
                    disabled={isUpscaling}
                  >
                    빠른 처리
                  </button>
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 border-2
                      ${upscaleOptions.quality === 'high'
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 border-gray-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, quality: 'high' })}
                    disabled={isUpscaling}
                  >
                    고품질
                  </button>
                </div>
              </div>

              <div className="bg-gray-100 rounded-xl p-4 my-5">
                {[
                  { label: '원본 크기:', value: `${uploadedImage.width} x ${uploadedImage.height}` },
                  { label: '출력 크기:', value: `${uploadedImage.width * upscaleOptions.scale} x ${uploadedImage.height * upscaleOptions.scale}` },
                  { label: '파일 크기:', value: formatFileSize(uploadedImage.size) },
                  { label: '예상 크기:', value: formatFileSize(estimateUpscaledSize(uploadedImage.size, upscaleOptions.scale)) },
                ].map((info, idx, arr) => (
                  <div key={info.label} className={`flex justify-between py-2 ${idx < arr.length - 1 ? 'border-b border-gray-200' : ''}`}>
                    <span className="text-sm text-gray-500">{info.label}</span>
                    <span className="text-sm font-semibold text-gray-900">{info.value}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2.5 max-[480px]:flex-col">
                <button
                  className="flex-1 p-3.5 text-base font-semibold border-none rounded-xl cursor-pointer transition-all duration-200 bg-gradient-to-br from-blue-500 to-purple-600 text-white hover:enabled:-translate-y-0.5 hover:enabled:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpscaleClick}
                  disabled={isUpscaling}
                >
                  {isUpscaling ? '처리 중...' : '업스케일 시작'}
                </button>
                <button
                  className="flex-1 p-3.5 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 bg-gray-100 text-gray-900 border-2 border-gray-200 hover:enabled:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleNewUpload}
                  disabled={isUpscaling}
                >
                  새 이미지
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
              <h3 className="text-lg text-gray-900 mb-5">미리보기</h3>
              <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-video flex items-center justify-center">
                <img
                  src={uploadedImage.dataUrl}
                  alt="Original"
                  className="max-w-full max-h-full object-contain"
                />
                <div className="absolute top-3 left-3 py-1.5 px-3 bg-black/70 text-white text-xs rounded-md">원본 이미지</div>
              </div>
            </div>
          </section>

          {/* Progress section */}
          {isUpscaling && (
            <section className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6 text-center">
              <h3 className="text-lg text-gray-900 mb-5">이미지 업스케일링 중...</h3>
              <div className="w-full h-2 bg-gray-200 rounded overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                  style={{ width: `${upscaleProgress}%` }}
                ></div>
              </div>
              <p className="text-xl font-semibold text-blue-500">{Math.round(upscaleProgress)}%</p>
            </section>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 py-3 px-4 bg-red-50 text-red-700 rounded-xl my-4 text-sm">
              <span className="text-lg">⚠️</span>
              {error}
            </div>
          )}

          {/* Result section */}
          {upscaledResult && (
            <section className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6">
              <h2 className="text-xl text-gray-900 mb-5 text-center">업스케일 완료!</h2>

              {/* Before/After comparison slider */}
              <div
                className="mb-6"
                ref={compareContainerRef}
                onMouseMove={handleCompareSlider}
                onTouchMove={handleCompareSliderTouch}
              >
                <div className="relative w-full aspect-video bg-gray-900 rounded-xl overflow-hidden cursor-ew-resize select-none">
                  <img
                    src={upscaledResult.dataUrl}
                    alt="Upscaled"
                    className="absolute top-0 left-0 w-full h-full object-contain"
                  />
                  <div
                    className="absolute top-0 left-0 h-full overflow-hidden"
                    style={{ width: `${compareSliderPosition}%` }}
                  >
                    <img
                      src={uploadedImage.dataUrl}
                      alt="Original"
                      className="absolute top-0 left-0 w-full h-full object-contain"
                      style={{ width: compareContainerRef.current?.offsetWidth }}
                    />
                  </div>
                  <div
                    className="absolute top-0 h-full w-0.5 bg-white -translate-x-1/2 pointer-events-none"
                    style={{ left: `${compareSliderPosition}%` }}
                  >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-xl text-gray-900">⟷</span>
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 right-3 flex justify-between pointer-events-none max-[480px]:flex-col max-[480px]:gap-1 max-[480px]:items-start">
                    <span className="py-1.5 px-3 bg-black/70 text-white text-xs rounded-md">원본</span>
                    <span className="py-1.5 px-3 bg-black/70 text-white text-xs rounded-md">업스케일</span>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">
                  슬라이더를 좌우로 드래그하여 비교하세요
                </p>
              </div>

              {/* Result info */}
              <div className="mt-6">
                <div className="grid grid-cols-4 gap-3 mb-5 max-md:grid-cols-2">
                  {[
                    { label: '확대 배율', value: `${upscaledResult.scale}x` },
                    { label: '원본 크기', value: `${uploadedImage.width} x ${uploadedImage.height}` },
                    { label: '출력 크기', value: `${upscaledResult.width} x ${upscaledResult.height}` },
                    { label: '출력 포맷', value: upscaledResult.format.toUpperCase() },
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-100 rounded-xl p-3 text-center">
                      <span className="block text-xs text-gray-500 mb-1">{item.label}</span>
                      <span className="block text-base font-semibold text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 max-[480px]:flex-col">
                  <button
                    className="flex-1 p-3.5 text-base font-semibold border-none rounded-xl cursor-pointer transition-all duration-200 bg-gradient-to-br from-blue-500 to-purple-600 text-white hover:-translate-y-0.5 hover:shadow-lg"
                    onClick={handleDownloadUpscaled}
                  >
                    다운로드
                  </button>
                  <button
                    className="flex-1 p-3.5 text-base font-semibold rounded-xl cursor-pointer transition-all duration-200 bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200"
                    onClick={handleNewUpload}
                  >
                    새 이미지 처리
                  </button>
                </div>

                {upscaledResult.message && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-xl text-center text-sm text-blue-600">
                    ℹ️ {upscaledResult.message}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Feature info */}
          {!upscaledResult && !isUpscaling && (
            <section className="bg-gray-100 rounded-2xl p-6 mt-6">
              <h3 className="text-base text-gray-900 mb-4">업스케일링 기능</h3>
              <div className="flex gap-4 max-md:flex-col">
                {[
                  { num: '1', title: 'AI 분석', desc: '이미지의 패턴과 디테일을 분석합니다.' },
                  { num: '2', title: '해상도 향상', desc: 'AI가 누락된 픽셀을 지능적으로 생성합니다.' },
                  { num: '3', title: '품질 최적화', desc: '선명도와 노이즈를 최적화하여 완성합니다.' },
                ].map((step) => (
                  <div key={step.num} className="flex-1 flex gap-3 items-start">
                    <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {step.num}
                    </span>
                    <div>
                      <h4 className="text-sm text-gray-900 mb-1">{step.title}</h4>
                      <p className="text-xs text-gray-500 leading-snug">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* 작업 히스토리 */}
      <section className="mt-8 pt-6 border-t border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">📋 업스케일 히스토리</h2>
        {isLoadingHistory ? (
          <div className="text-center py-8 text-gray-400 text-sm">불러오는 중...</div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">아직 업스케일 기록이 없습니다.</div>
        ) : (
          <div className="space-y-3">
            {history.map((item) => (
              <div key={item.jobId} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                    item.status === 'completed' ? 'bg-green-100 text-green-700' :
                    item.status === 'failed' ? 'bg-red-100 text-red-700' :
                    item.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {item.status === 'completed' ? '완료' : item.status === 'failed' ? '실패' : item.status === 'processing' ? '처리중' : '대기'}
                  </span>
                  <span className="text-sm text-gray-700 truncate">
                    {item.parameters?.scale ? `${item.parameters.scale}x 업스케일` : '이미지 업스케일'}
                    {item.parameters?.format ? ` · ${item.parameters.format.toUpperCase()}` : ''}
                  </span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-4">
                  {new Date(item.createdAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ImageUpscaler;
